import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';
import type { SubscriptionEventHistoryItem } from '../types';
import {
  copyToClipboard,
  formatDateTime,
  formatMetadataJson,
  formatShortId,
} from '../utils/helpers';
import { SubscriptionEventSourceBadge } from './SubscriptionEventSourceBadge';
import { SubscriptionEventTypeBadge } from './SubscriptionEventTypeBadge';

interface SubscriptionEventDetailDrawerProps {
  event: SubscriptionEventHistoryItem;
  productLabel: string | null;
  onClose: () => void;
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="text-xs font-medium text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm text-foreground break-all text-end sm:max-w-[65%] font-mono text-xs">
        {value?.trim() ? value : '—'}
      </span>
    </div>
  );
}

function CopyableIdRow({ label, value }: { label: string; value: string | null | undefined }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const full = value?.trim() || null;
  const short = formatShortId(full);

  if (!full) {
    return <DetailRow label={label} value={null} />;
  }

  const handleCopy = async () => {
    const ok = await copyToClipboard(full);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-xs font-medium text-muted-foreground shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0 justify-end">
        <code className="truncate font-mono text-xs text-foreground" title={full}>
          {short ?? full}
        </code>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="shrink-0 rounded-md border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('subscriptionEvents.detail.copyId', { label })}
        >
          {copied ? t('subscriptionEvents.detail.copied') : t('subscriptionEvents.detail.copy')}
        </button>
      </div>
    </div>
  );
}

export function SubscriptionEventDetailDrawer({
  event,
  productLabel,
  onClose,
}: SubscriptionEventDetailDrawerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const panelRef = useRef<HTMLDivElement>(null);
  const [jsonCopied, setJsonCopied] = useState(false);

  const metadataJson = formatMetadataJson(event.metadata);
  const hasMetadata = metadataJson.length > 0;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKey = (keydown: KeyboardEvent) => {
      if (keydown.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    panelRef.current?.focus();
  }, [event.id]);

  const handleCopyJson = async () => {
    if (!hasMetadata) return;
    const ok = await copyToClipboard(metadataJson);
    if (ok) {
      setJsonCopied(true);
      window.setTimeout(() => setJsonCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" role="presentation">
      <button
        type="button"
        tabIndex={-1}
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-event-detail-title"
        className={cn(
          'relative ms-auto flex h-full w-full max-w-lg flex-col border-s border-border bg-background shadow-xl',
          'animate-in slide-in-from-end duration-200',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-4 sm:px-5">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('subscriptionEvents.eyebrow')}
            </p>
            <h2
              id="subscription-event-detail-title"
              className="text-lg font-semibold text-foreground truncate"
            >
              {t('subscriptionEvents.detail.title')}
            </h2>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <SubscriptionEventTypeBadge eventType={event.eventType} />
              <SubscriptionEventSourceBadge eventSource={event.eventSource} />
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('common.close')}>
            <Icons.X className="h-5 w-5" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 space-y-5">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('subscriptionEvents.detail.generalSection')}
            </h3>
            <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('subscriptionEvents.columns.eventType')}
                </span>
                <SubscriptionEventTypeBadge eventType={event.eventType} />
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('subscriptionEvents.columns.eventSource')}
                </span>
                <SubscriptionEventSourceBadge eventSource={event.eventSource} />
              </div>
              <DetailRow label={t('subscriptionEvents.columns.status')} value={event.eventStatus} />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('subscriptionEvents.detail.referencesSection')}
            </h3>
            <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
              <CopyableIdRow
                label={t('subscriptionEvents.filters.tenantId')}
                value={event.tenantId}
              />
              <CopyableIdRow
                label={t('subscriptionEvents.filters.productId')}
                value={event.productId}
              />
              <CopyableIdRow
                label={t('subscriptionEvents.filters.subscriptionId')}
                value={event.subscriptionId}
              />
              {productLabel ? (
                <DetailRow label={t('subscriptionEvents.columns.product')} value={productLabel} />
              ) : null}
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('subscriptionEvents.detail.datesSection')}
            </h3>
            <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
              <DetailRow
                label={t('subscriptionEvents.detail.occurredAt')}
                value={formatDateTime(event.occurredAt, locale)}
              />
              <DetailRow
                label={t('subscriptionEvents.detail.createdAt')}
                value={formatDateTime(event.createdAt, locale)}
              />
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t('subscriptionEvents.detail.metadataSection')}
              </h3>
              {hasMetadata ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleCopyJson()}
                >
                  {jsonCopied
                    ? t('subscriptionEvents.detail.copiedJson')
                    : t('subscriptionEvents.detail.copyJson')}
                </Button>
              ) : null}
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              {hasMetadata ? (
                <pre className="overflow-x-auto text-xs font-mono text-foreground whitespace-pre-wrap break-words">
                  {metadataJson}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  {t('subscriptionEvents.detail.noMetadata')}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
